import React, { useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import { ChartColumn } from 'lucide-react';
import { EmptyState } from '../common';
import { formatPriceName } from '../../utils/priceName';
import { GRID_COLOR, TICK_COLOR, LABEL_COLOR, FONT_FAMILY, MARY_COLOR, OTHER_COLOR, AVG_LINE_COLOR } from './elements/chartTheme';

Chart.register(...registerables);

// The average series is a quiet neutral line; Mary products are flagged with the brand color
// (same color for every Mary point), so they pop against it.
const LINE_COLOR = OTHER_COLOR;

// Draws the USD value above each point, mirroring the Power BI "Precios promedio por SKU" view.
const valueLabels = {
    id: 'skuValueLabels',
    afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const meta = chart.getDatasetMeta(0);
        if (!meta || meta.hidden) return;
        ctx.save();
        ctx.font = `600 9px ${FONT_FAMILY}`;
        ctx.fillStyle = LABEL_COLOR;
        ctx.textAlign = 'center';
        meta.data.forEach((pt, i) => {
            const v = chart.data.datasets[0].data[i];
            if (v == null) return;
            ctx.fillText(Number(v).toFixed(2), pt.x, pt.y - 8);
        });
        ctx.restore();
    }
};

export default function PriceBySkuChart({ rows = [], overallAvg = 0, onPointClick }) {
    const ref = useRef(null);
    // Keep the latest click handler without re-creating the chart on every render.
    const clickRef = useRef(onPointClick);
    clickRef.current = onPointClick;

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas || !rows.length) return undefined;
        if (canvas.chart) canvas.chart.destroy();

        const labels = rows.map((r) => formatPriceName(r));
        const data = rows.map((r) => r.avgUsd);
        // Mary products get their own dot color (uniform across all Mary points).
        const pointColors = rows.map((r) => (r.isMary ? MARY_COLOR : LINE_COLOR));

        canvas.chart = new Chart(canvas, {
            type: 'line',
            data: {
                labels,
                datasets: [
                    {
                        label: 'Precio promedio (USD)',
                        data,
                        borderColor: LINE_COLOR,
                        backgroundColor: LINE_COLOR,
                        pointBackgroundColor: pointColors,
                        pointBorderColor: pointColors,
                        borderWidth: 2,
                        tension: 0.25,
                        pointRadius: rows.map((r) => (r.isMary ? 4 : 3)),
                        pointHoverRadius: 6
                    },
                    {
                        // Dashed reference line at the overall average, like the BI view.
                        label: `Promedio general ($${Number(overallAvg).toFixed(2)})`,
                        data: labels.map(() => overallAvg),
                        borderColor: AVG_LINE_COLOR,
                        backgroundColor: AVG_LINE_COLOR,
                        borderWidth: 1.5,
                        borderDash: [6, 4],
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        tension: 0,
                        fill: false
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: { top: 24 } },
                interaction: { mode: 'index', intersect: false },
                onHover: (evt, elements) => {
                    if (evt?.native?.target) evt.native.target.style.cursor = elements.length ? 'pointer' : 'default';
                },
                onClick: (evt, elements) => {
                    if (!elements.length || !clickRef.current) return;
                    const idx = elements[0].index;
                    const row = rows[idx];
                    if (row) clickRef.current(row);
                },
                plugins: {
                    legend: {
                        position: 'top',
                        align: 'end',
                        labels: { boxWidth: 12, boxHeight: 2, color: TICK_COLOR, font: { size: 11, family: FONT_FAMILY } }
                    },
                    tooltip: {
                        callbacks: {
                            label: (item) => {
                                if (item.datasetIndex !== 0) return null;
                                const row = rows[item.dataIndex];
                                return `$${Number(row.avgUsd).toFixed(2)} · ${row.count} precio(s)${row.isMary ? ' · Mary' : ''}`;
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        title: { display: true, text: 'USD', color: TICK_COLOR, font: { family: FONT_FAMILY } },
                        grid: { color: GRID_COLOR },
                        border: { display: false },
                        ticks: { color: TICK_COLOR, font: { family: FONT_FAMILY }, callback: (v) => `$${Number(v).toFixed(2)}` }
                    },
                    x: {
                        ticks: {
                            maxRotation: 90,
                            minRotation: 90,
                            autoSkip: false,
                            // Mary product labels are bold and share the Mary dot color.
                            font: (ctx) => ({ size: 9, family: FONT_FAMILY, weight: rows[ctx.index]?.isMary ? 'bold' : 'normal' }),
                            color: (ctx) => (rows[ctx.index]?.isMary ? MARY_COLOR : TICK_COLOR)
                        },
                        grid: { display: false },
                        border: { color: GRID_COLOR }
                    }
                }
            },
            plugins: [valueLabels]
        });

        return () => canvas.chart?.destroy();
    }, [rows, overallAvg]);

    if (!rows.length) {
        return (
            <EmptyState
                icon={<ChartColumn />}
                title="Sin precios para los filtros actuales"
                description="Amplía el rango de fechas o quita algún filtro."
            />
        );
    }

    // Always fill the container's full width; only exceed it (and scroll) when there are
    // enough SKUs that each needs ~26px. Few SKUs → the chart still spans 100%.
    const minWidth = Math.max(720, rows.length * 26);
    return (
        <div className="overflow-x-auto overflow-y-hidden">
            <div style={{ width: `max(100%, ${minWidth}px)`, height: 440 }}>
                <canvas ref={ref} />
            </div>
        </div>
    );
}
